/**
 * 消息发送 Hook
 *
 * 职责：
 * 1. 管理消息发送状态（isSending）
 * 2. 调用 API 发送消息并启动 Agent
 * 3. 创建乐观更新的用户消息
 * 4. 处理发送成功后的 UI 更新（清空输入框、滚动）
 * 5. 设置 Agent Run ID 以启动流式响应
 * 6. 处理发送错误（会话限制、网络错误等）
 * 7. 重置线程状态
 *
 * 使用场景：
 * - 用户在聊天输入框中发送消息时
 * - 需要启动新的 Agent 对话时
 */
import { useCallback, useState, RefObject } from 'react';
import { toast } from 'sonner';
import { SessionLimitError } from '@/lib/api';
import { sendMessages, getThread } from '@/services/thread';
import { safeJsonParse } from '@/components/thread/utils';
import { useStores } from '@/store/hooks/useStore';
import { initialValue } from '@/components/slot-input/slot-input';
import type { ChatInputHandles } from '@/components/thread/chat-input/chat-input';
import { useI18n } from '@/hooks/use-i18n';
import { eventBus, EventTypes } from '@/lib/event-bus';

interface UseThreadMessageSenderParams {
  threadId: string;
  setAgentRunId: (id: string | null) => void;
  scrollToBottom: (behavior?: 'auto' | 'smooth') => void;
  chatInputRef: RefObject<ChatInputHandles>;
}

interface UseThreadMessageSenderReturn {
  isSending: boolean;
  handleSubmitMessage: THREAD.SendMessageFn;
}

export const useThreadMessageSender = ({
  threadId,
  setAgentRunId,
  scrollToBottom,
  chatInputRef,
}: UseThreadMessageSenderParams): UseThreadMessageSenderReturn => {
  const stores = useStores();
  const { addMessage, updateThreadInfo } = stores.threadStore;
  const { resetThreadStatus } = stores.threadStore;
  const { t } = useI18n('thread');

  const [isSending, setIsSending] = useState(false);

  // 检查消息内容中是否包含@agent，如果有则刷新agent数据
  const checkAndRefreshAgentData = useCallback(
    async (
      contents: Array<{ content: string; metadata?: { is_hide?: boolean } }>,
    ) => {
      try {
        let hasAgentMention = false;

        // 遍历所有消息内容，检查是否有 @agent 提及
        for (const item of contents) {
          const parsedContent = safeJsonParse(item.content, {} as any);
          const contentItems = safeJsonParse<MESSAGE.UserContentInnerItem[]>(
            parsedContent.content,
            [] as MESSAGE.UserContentInnerItem[],
          );

          if (
            contentItems.some((contentItem) => contentItem?.type === 'agent')
          ) {
            hasAgentMention = true;
            break;
          }
        }

        if (hasAgentMention) {
          // 异步刷新，不阻塞主流程
          const threadData = await getThread(threadId);
          if (threadData) {
            updateThreadInfo({
              agents: threadData.agents,
              functions: threadData.functions,
            });
          }
        }
      } catch (err) {
        console.error('检查agent引用失败:', err);
      }
    },
    [threadId, updateThreadInfo],
  );

  const handleSubmitMessage: THREAD.SendMessageFn = useCallback(
    async (messages, options) => {
      try {
        if (!messages || messages.length === 0) {
          return null;
        }

        // 重置线程状态并开始发送
        resetThreadStatus();
        setIsSending(true);

        // 调用 API 发送消息
        const result = await sendMessages({
          contents: messages,
          thread_id: threadId,
        });

        if (!result) {
          throw new Error('发送消息失败');
        }

        // 检查消息中是否@了某个agent，如果有则刷新AgentList信息 这里就不await了，大模型回答的比较慢
        checkAndRefreshAgentData(result.messages || messages);

        // 循环添加所有返回的消息
        messages.forEach((msg, idx) => {
          const resultMsg = result.messages[idx];
          const optimisticUserMessage: MESSAGE.Message = {
            message_id: resultMsg.message_id,
            thread_id: result.thread_id,
            type: 'user',
            is_llm_message: false,
            content: safeJsonParse(resultMsg.content || msg.content, {} as any),
            metadata: resultMsg.metadata || msg.metadata || {},
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };



          // 添加消息到 store
          // FIXME: 不处理成批量是因为后续构造消息树逻辑太复杂
          addMessage(optimisticUserMessage);
        });

        // 获取最后一条消息
        const lastMessage = result.messages?.[result.messages.length - 1];

        // 清空输入框内容
        chatInputRef.current?.setContent(initialValue);

        // 滚动到底部
        // FIX: 这样做是为了防止消息还没渲染出来就滚动到底部，导致没能实际滚动到底部
        for (let i = 0; i < 5; i++) {
          setTimeout(() => {
            scrollToBottom('smooth');
          }, i * 10);
        }

        // 用户主动发送消息后，立即隐藏外部 Agent icon（不等后端 metadata 更新）
        updateThreadInfo({ metadata: { last_source_msg_type: 'system' } });
        eventBus.emit(EventTypes.USER_SENT_MESSAGE, { threadId });

        // 设置 Agent Run ID，触发流式响应
        setAgentRunId(result.agent_run_id);

        return {
          messageId: lastMessage?.message_id || '',
          agentRunId: result.agent_run_id,
        };
      } catch (err) {
        console.error('Error sending message or starting agent:', err);

        // 处理会话限制错误
        if (err instanceof SessionLimitError) {
          toast.error(t('sessionLimitReached'));
          return null;
        }

        // 处理其他错误（第一个 if 已 return，此处无需重复判断 SessionLimitError）
        toast.error(err instanceof Error ? err.message : 'Operation failed');
        return null;
      } finally {
        setIsSending(false);
      }
    },
    [
      threadId,
      addMessage,
      resetThreadStatus,
      scrollToBottom,
      setAgentRunId,
      chatInputRef,
      checkAndRefreshAgentData,
    ],
  );

  return {
    isSending,
    handleSubmitMessage,
  };
};
